import { useEffect, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { getDriversDetails } from "./driverApi";
import vehicleService from "../../services/vehicles/vehicleservice";
import type { DriverInfoDto } from "../../models/Delivery-Executive/DeliveryExecutive";
import type { DriverAssignment } from "./route-preview";

export type PlanningConfig = { assignments: DriverAssignment[]; deliveryMinutes: number | null; breakMinutes: number | null };
export const initialPlanning: PlanningConfig = { assignments: [], deliveryMinutes: 5, breakMinutes: 30 };
export function planningReady(plan: PlanningConfig): boolean {
  return plan.assignments.length > 0 && plan.assignments.length <= 5 && plan.assignments.every(a => !!a.vehicleNo) &&
    new Set(plan.assignments.map(a => a.vehicleNo)).size === plan.assignments.length &&
    [plan.deliveryMinutes, plan.breakMinutes].every(n => n !== null && Number.isFinite(n) && n >= 0 && n <= 1440);
}

export default function SmartMapPlanning({ value, onChange, disabled, lockAssignments = false, onLoadingChange }: {
  value: PlanningConfig; onChange: (value: PlanningConfig) => void; disabled: boolean; lockAssignments?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}) {
  const [drivers, setDrivers] = useState<DriverInfoDto[]>([]);
  const [vehicles, setVehicles] = useState<{ vehicleNumber: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const missingVehicles = value.assignments.filter(a => !a.vehicleNo).map(a => a.driver.name);
  useEffect(() => { onLoadingChange?.(loading); }, [loading, onLoadingChange]);
  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([getDriversDetails(), vehicleService.getVehicleDropdown({})]).then(([executives, fleet]) => {
      if (cancelled) return;
      const people = executives.status === "fulfilled" ? executives.value : [];
      const cars = fleet.status === "fulfilled" ? fleet.value?.data ?? [] : [];
      setDrivers(people); setVehicles(cars); setLoading(false);
      if (!people.length || !cars.length) setError("Executive or vehicle choices are unavailable. Refresh to try again.");
    });
    return () => { cancelled = true; };
  }, []);
  return <section className="smart-map-planning" aria-label="Route planning setup">
    <div className="smart-map-executives"><label htmlFor="planning-executives">Delivery executives</label>
      <MultiSelect inputId="planning-executives" aria-label="Delivery executives" disabled={disabled || lockAssignments || loading}
        value={value.assignments.map(a => a.driver.driverId)} options={drivers.map(d => ({ ...d,
          label: `${d.name} — ${d.phonenumber} — ${d.posName || d.posCode || "POS unavailable"}` }))}
        optionLabel="label" optionValue="driverId" filter display="chip" selectionLimit={5} showSelectAll={false}
        panelClassName="smart-map-executive-panel" filterPlaceholder="Search name, phone or POS"
        itemTemplate={(driver: DriverInfoDto) => <div className="map-executive-option" aria-label={`${driver.name} — ${driver.phonenumber} — ${driver.posName || driver.posCode || "POS unavailable"}`}>
          <span className="map-executive-avatar" aria-hidden="true">{driver.name?.slice(0,1).toUpperCase()}</span>
          <span className="map-executive-person"><strong>{driver.name}</strong><small><i className="pi pi-phone" aria-hidden="true" /> {driver.phonenumber}</small></span>
          <span className="map-executive-pos">{driver.posName || driver.posCode || "POS unavailable"}</span>
        </div>}
        placeholder="Choose executive(s) before drawing" onChange={e => onChange({ ...value,
          assignments: (e.value as number[]).map(id => value.assignments.find(a => a.driver.driverId === id) ||
            { driver: drivers.find(d => d.driverId === id)!, vehicleNo: "" }) })} />
    </div>
    <div className="smart-map-vehicles">
      {value.assignments.length ? value.assignments.map(a => <div key={a.driver.driverId}>
        <label htmlFor={`planning-vehicle-${a.driver.driverId}`}>Vehicle · {a.driver.name}</label>
        <Dropdown inputId={`planning-vehicle-${a.driver.driverId}`} aria-label={`Vehicle for ${a.driver.name}`}
          className={!loading && !a.vehicleNo ? "p-invalid" : undefined} aria-required aria-invalid={!loading && !a.vehicleNo}
          aria-describedby="planning-setup-note"
          value={a.vehicleNo} options={vehicles.filter(v => v.vehicleNumber === a.vehicleNo ||
            !value.assignments.some(other => other.driver.driverId !== a.driver.driverId && other.vehicleNo === v.vehicleNumber))}
          optionLabel="vehicleNumber" optionValue="vehicleNumber" filter placeholder="Choose vehicle" disabled={disabled || lockAssignments || loading}
          onChange={e => onChange({ ...value, assignments: value.assignments.map(item =>
            item.driver.driverId === a.driver.driverId ? { ...item, vehicleNo: e.value } : item) })} />
      </div>) : <div><label>Vehicle</label><Dropdown placeholder="Choose an executive first" disabled /></div>}
    </div>
    <div><label htmlFor="order-delivery-minutes">Delivery time for an order</label>
      <InputNumber inputId="order-delivery-minutes" value={value.deliveryMinutes} suffix=" min" min={0} max={1440} maxFractionDigits={1}
        disabled={disabled} onValueChange={e => onChange({ ...value, deliveryMinutes: e.value ?? null })} /></div>
    <div><label htmlFor="route-break-minutes">Break time</label>
      <InputNumber inputId="route-break-minutes" value={value.breakMinutes} suffix=" min" min={0} max={1440} maxFractionDigits={1}
        disabled={disabled} onValueChange={e => onChange({ ...value, breakMinutes: e.value ?? null })} /></div>
    <p id="planning-setup-note" className="smart-map-planning-note" role={error ? "alert" : "status"}>{error || (planningReady(value)
      ? "Ready to draw · One break allowance per route. Times are planning estimates."
      : missingVehicles.length ? `Choose a vehicle for ${missingVehicles.join(", ")} to enable polygon drawing.`
      : "Choose executive(s) and a different vehicle for each before drawing a polygon.")}</p>
  </section>;
}
